import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface PlanOption {
  key: 'mensal' | 'semestral' | 'anual'
  label: string
  months: number
  discount_pct: number
}

export interface Pricing {
  first_service: number
  extra_service: number
  plans: PlanOption[]
}

/** Um serviço marcado, já com o preço que ELE custa na ordem em que entrou. */
export interface SelectedService {
  id: number
  label: string
  categoryLabel: string
  price: number
}

export interface CouponState {
  checking: boolean
  valid: boolean | null
  discountPct: number | null
  /** Cortesia: meses grátis em vez de percentual. */
  freeMonths: number | null
}

export const formatBRL = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/* ─────────────────────────── Cálculo (espelho do servidor) ─────────────────────────── */

/**
 * Espelho de Explore::PricingTable. O servidor recalcula tudo no create e
 * nunca confia neste número — ele existe só para a pessoa ver o preço mudar
 * enquanto escolhe.
 */
export function calculateSummary(
  pricing: Pricing,
  servicesCount: number,
  planKey: PlanOption['key'],
  couponState: Pick<CouponState, 'valid' | 'discountPct'>,
) {
  const plan = pricing.plans.find((p) => p.key === planKey) ?? pricing.plans[0]
  // Sem serviço nenhum o total é zero: mostrar R$ 8,90 antes de a pessoa marcar
  // algo cobraria por um carrinho vazio.
  const extraCount = Math.max(servicesCount - 1, 0)
  const subtotal =
    servicesCount === 0
      ? 0
      : pricing.first_service + pricing.extra_service * extraCount

  let monthly = subtotal * (1 - plan.discount_pct / 100)
  const couponPct = couponState.valid ? (couponState.discountPct ?? 0) : 0
  if (couponPct > 0) monthly *= 1 - couponPct / 100
  monthly = Math.round(monthly * 100) / 100

  const periodTotal = Math.round(monthly * plan.months * 100) / 100

  return {
    plan,
    subtotal,
    extraCount,
    servicesCount,
    monthly,
    periodTotal,
    couponPct,
  }
}

export type PricingSummary = ReturnType<typeof calculateSummary>

interface PriceSummaryProps {
  pricing: Pricing
  services: SelectedService[]
  plan: PlanOption['key']
  onPlanChange: (plan: PlanOption['key']) => void
  couponCode: string
  onCouponCodeChange: (value: string) => void
  couponState: CouponState
  onCheckCoupon: () => void
  summary: PricingSummary
  /**
   * A gaveta do celular é CONTROLADA de fora: quem mostra o total e abre a
   * gaveta é a barra de ação do wizard (WizardFooter). Duas barras fixas
   * empilhadas no rodapé — uma com o preço, outra com "Continuar" — comiam um
   * terço da tela do celular e faziam a pessoa escolher entre ver o preço e ver
   * o botão.
   */
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * O resumo do valor do wizard, nas duas telas que ele precisa ter.
 *
 * No desktop é a coluna da direita, `sticky` — acompanha a rolagem do passo em
 * vez de sumir no topo. No celular é uma GAVETA que sobe do rodapé, aberta pelo
 * total que fica na barra de ação (WizardFooter). Antes ele era só a mesma
 * coluna empilhada no fim da página, ou seja: quem marcava serviços pelo
 * celular — a maioria — nunca via o preço mudar enquanto escolhia.
 */
export default function PriceSummary(props: PriceSummaryProps) {
  const { open, onOpenChange } = props

  // Rolagem do fundo travada enquanto a gaveta está aberta: sem isso o dedo
  // arrasta a página atrás em vez da lista de dentro.
  useEffect(() => {
    if (!open) return

    const anterior = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('keydown', onEsc)

    return () => {
      document.body.style.overflow = anterior
      document.removeEventListener('keydown', onEsc)
    }
  }, [open, onOpenChange])

  return (
    <>
      {/* Desktop: a coluna de sempre, colada no topo ao rolar. */}
      <aside className="hidden h-fit rounded-2xl border border-gray-200 bg-white p-6 lg:sticky lg:top-6 lg:block">
        <h2 className="text-base font-bold text-gray-900">Resumo do valor</h2>
        <SummaryBody {...props} />
      </aside>

      {/* Celular: a gaveta. Só existe no DOM quando aberta — fechada ela não
          tem barra própria, quem mostra o total é o WizardFooter. */}
      {open && (
        <div className="lg:hidden">
          <div
            aria-hidden="true"
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-40 bg-black/40"
          />

          <div
            id="resumo-do-valor"
            role="dialog"
            aria-modal="true"
            aria-label="Resumo do valor"
            className={cn(
              'fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-gray-200 bg-white',
              'shadow-[0_-8px_24px_rgba(0,0,0,0.12)] pb-[env(safe-area-inset-bottom)]',
            )}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <h2 className="text-base font-bold text-gray-900">
                Resumo do valor
              </h2>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Fechar resumo"
                className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto border-t border-gray-100 px-4 pb-6">
              <SummaryBody {...props} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/* ─────────────────────────── Corpo (o mesmo nas duas telas) ─────────────────────────── */

function SummaryBody({
  pricing,
  services,
  plan,
  onPlanChange,
  couponCode,
  onCouponCodeChange,
  couponState,
  onCheckCoupon,
  summary,
}: PriceSummaryProps) {
  // Cortesia cobre um período fixo (6 meses = semestral): deixar o seletor
  // aberto prometeria "12x R$ 0,00" para um brinde de 6 meses. O servidor força
  // o mesmo plano no create — aqui é só não mentir na tela.
  const planoTravado =
    couponState.valid === true && couponState.freeMonths != null

  return (
    <>
      <ServicesList services={services} />

      <div className="mt-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Plano
        </p>
        <div className="mt-2 space-y-2">
          {pricing.plans.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onPlanChange(option.key)}
              disabled={planoTravado && option.key !== plan}
              className={cn(
                'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm font-semibold transition-colors',
                plan === option.key
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-gray-200 text-gray-700 hover:border-primary/40',
                planoTravado &&
                  option.key !== plan &&
                  'cursor-not-allowed opacity-40 hover:border-gray-200',
              )}
            >
              {option.label}
              {option.discount_pct > 0 && (
                <span className="text-xs font-bold text-emerald-600">
                  -{option.discount_pct}%
                </span>
              )}
            </button>
          ))}
        </div>
        {planoTravado && (
          <p className="mt-1.5 text-xs text-gray-500">
            O cupom cobre {couponState.freeMonths} meses — o plano acompanha a
            cortesia.
          </p>
        )}
      </div>

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Cupom de desconto
        </p>
        <div className="mt-2 flex gap-2">
          <input
            value={couponCode}
            onChange={(e) => onCouponCodeChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onCheckCoupon()
              }
            }}
            placeholder="Digite o código do cupom"
            aria-label="Código do cupom"
            className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm uppercase outline-none focus:border-gray-400"
          />
          <button
            type="button"
            onClick={onCheckCoupon}
            disabled={couponState.checking}
            className="shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {couponState.checking ? '…' : 'Aplicar'}
          </button>
        </div>
        {couponState.valid === true && (
          <p className="mt-1 text-xs font-semibold text-emerald-600">
            {couponState.freeMonths != null
              ? `Cupom aplicado: ${couponState.freeMonths} meses grátis`
              : `Cupom aplicado: -${couponState.discountPct}%`}
          </p>
        )}
        {couponState.valid === false && (
          <p className="mt-1 text-xs text-red-600">
            Cupom inválido, expirado ou esgotado
          </p>
        )}
      </div>

      <div className="mt-5 space-y-2 border-t pt-4 text-sm">
        <div className="flex items-center justify-between text-gray-500">
          <span>Subtotal</span>
          <span>{formatBRL(summary.subtotal)}</span>
        </div>
        {summary.plan.discount_pct > 0 && (
          <div className="flex items-center justify-between text-xs text-emerald-600">
            <span>
              Desconto plano {summary.plan.label.toLowerCase()} (
              {summary.plan.discount_pct}%)
            </span>
            <span>
              -{formatBRL(summary.subtotal * (summary.plan.discount_pct / 100))}
            </span>
          </div>
        )}
        {summary.couponPct > 0 && (
          <div className="flex items-center justify-between text-xs text-emerald-600">
            <span>
              Cupom{' '}
              {couponState.freeMonths != null
                ? `(${couponState.freeMonths} meses grátis)`
                : `(${summary.couponPct}%)`}
            </span>
            <span>
              -
              {formatBRL(
                summary.subtotal * (1 - summary.plan.discount_pct / 100) -
                  summary.monthly,
              )}
            </span>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-1 border-t pt-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{summary.plan.label}</span>
          <span className="font-semibold text-gray-900">
            {formatBRL(summary.monthly)}/mês
          </span>
        </div>
        <div className="flex items-center justify-between text-base font-bold text-gray-900">
          <span>Total do período ({summary.plan.months}x)</span>
          <span>{formatBRL(summary.periodTotal)}</span>
        </div>
        {summary.periodTotal === 0 && summary.servicesCount > 0 && (
          <p className="pt-1 text-xs text-emerald-600">
            Nada a pagar agora — sua página entra no ar assim que a IA terminar.
          </p>
        )}
      </div>
    </>
  )
}

/**
 * O que a pessoa está levando, item por item.
 *
 * Substitui os três contadores "Categorias / Subcategorias / Localizações":
 * eram herança do modelo antigo, em que a CATEGORIA era a unidade cobrada.
 * Hoje quem cobra é o serviço, "Localizações" era sempre 0 ou 1 e
 * "Subcategorias" é palavra de banco de dados.
 */
function ServicesList({ services }: { services: SelectedService[] }) {
  if (services.length === 0) {
    return (
      <p className="mt-4 rounded-lg bg-gray-50 px-3 py-3 text-xs text-gray-500">
        Marque os serviços que você oferece — eles aparecem aqui com o preço de
        cada um.
      </p>
    )
  }

  // Agrupa mantendo a ORDEM em que os serviços foram marcados: é ela que
  // decide quem paga o primeiro preço e quem paga o adicional.
  const grupos: { category: string; items: SelectedService[] }[] = []
  services.forEach((service) => {
    const grupo = grupos.find((g) => g.category === service.categoryLabel)
    if (grupo) grupo.items.push(service)
    else grupos.push({ category: service.categoryLabel, items: [service] })
  })

  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        Seus serviços ({services.length})
      </p>
      <div className="mt-2 space-y-3">
        {grupos.map((grupo) => (
          <div key={grupo.category}>
            <p className="text-xs font-semibold text-gray-700">
              {grupo.category}
            </p>
            <ul className="mt-1 space-y-1">
              {grupo.items.map((service) => (
                <li
                  key={service.id}
                  className="flex items-baseline justify-between gap-2 text-xs text-gray-600"
                >
                  <span className="min-w-0 truncate">{service.label}</span>
                  <span className="shrink-0 tabular-nums">
                    {formatBRL(service.price)}/mês
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}
